import { useState } from 'react';
import { Link } from 'react-router-dom';
import { trpc } from '@/trpc/client';

interface ListItem {
  userId: string;
  displayName: string | null;
  pictureUrl: string | null;
  state: { state: string; lastEventAt: string } | null;
}

/**
 * LINE user picker. Two modes:
 *  - Search (operator types in the box) → `trpc.lineUser.search` returns matches.
 *  - Browse (default) → `trpc.lineUser.list` returns the 20 most-recent users
 *    sorted by lastEventAt, with cursor-based pagination.
 *
 * Each row shows the LINE displayName + avatar (when LINE getProfile succeeds)
 * alongside the user-ID so the operator can pick by face rather than opaque
 * `U...` strings.
 */
export function LineUserList(): JSX.Element {
  const [query, setQuery] = useState('');
  const [cursors, setCursors] = useState<string[]>([]); // history stack for "previous page"
  const [activeCursor, setActiveCursor] = useState<string | undefined>(undefined);

  const isSearching = query.trim().length > 0;
  const searchQ = trpc.lineUser.search.useQuery({ query }, { enabled: isSearching });
  const listQ = trpc.lineUser.list.useQuery(
    { cursor: activeCursor, limit: 20 },
    { enabled: !isSearching },
  );

  const items: ListItem[] = isSearching ? (searchQ.data?.items ?? []) : (listQ.data?.items ?? []);
  const nextCursor = !isSearching ? (listQ.data?.cursor ?? null) : null;
  const loading = isSearching ? searchQ.isLoading : listQ.isLoading;
  const error = (isSearching ? searchQ.error : listQ.error)?.message;

  const goNext = () => {
    if (!nextCursor) return;
    setCursors((c) => [...c, activeCursor ?? '']);
    setActiveCursor(nextCursor);
  };
  const goPrev = () => {
    setCursors((c) => {
      const last = c[c.length - 1];
      setActiveCursor(last && last.length > 0 ? last : undefined);
      return c.slice(0, -1);
    });
  };
  const resetPagination = () => {
    setCursors([]);
    setActiveCursor(undefined);
  };

  return (
    <div>
      <h1 className="text-2xl font-bold">LINE ユーザー</h1>
      <p className="mt-1 text-sm text-gray-600">
        最近 LINE で連絡を受けたユーザー一覧。ID で絞り込み検索もできます。
      </p>
      <div className="mt-4 max-w-md">
        <input
          type="text"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            resetPagination();
          }}
          placeholder="userId の先頭で絞り込み（空欄なら最近のユーザー）"
          aria-label="LINE userId prefix"
          className="w-full rounded border border-gray-300 px-3 py-2 text-sm focus:border-gray-900 focus:outline-none"
        />
      </div>
      <div className="mt-4">
        {loading ? (
          <p className="text-sm text-gray-500">読み込み中…</p>
        ) : error ? (
          <p className="text-sm text-red-600">エラー: {error}</p>
        ) : items.length === 0 ? (
          <p className="text-sm text-gray-500">該当ユーザーはありません。</p>
        ) : (
          <ul className="divide-y divide-gray-200 rounded border border-gray-200 bg-white">
            {items.map((it) => (
              <li key={it.userId}>
                <Link
                  to={`/line-users/${encodeURIComponent(it.userId)}`}
                  className="flex items-center gap-3 px-4 py-2 hover:bg-gray-50"
                >
                  {it.pictureUrl ? (
                    <img
                      src={it.pictureUrl}
                      alt=""
                      className="h-8 w-8 shrink-0 rounded-full bg-gray-100 object-cover"
                    />
                  ) : (
                    <div className="h-8 w-8 shrink-0 rounded-full bg-gray-200" />
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm text-gray-900">
                      {it.displayName ?? (
                        <span className="text-gray-400">(displayName 取得不可)</span>
                      )}
                    </div>
                    <div className="truncate font-mono text-xs text-gray-500">{it.userId}</div>
                  </div>
                  {it.state ? (
                    <div className="shrink-0 text-xs text-gray-500">
                      <div className="font-medium text-gray-700">{it.state.state}</div>
                      <div className="text-gray-400">{formatDate(it.state.lastEventAt)}</div>
                    </div>
                  ) : null}
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>
      {!isSearching ? (
        <div className="mt-3 flex items-center gap-2 text-sm">
          <button
            type="button"
            onClick={goPrev}
            disabled={cursors.length === 0}
            className="rounded border border-gray-300 px-3 py-1 text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            前のページ
          </button>
          <button
            type="button"
            onClick={goNext}
            disabled={!nextCursor}
            className="rounded border border-gray-300 px-3 py-1 text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            次のページ
          </button>
        </div>
      ) : null}
    </div>
  );
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleString('ja-JP', { hour12: false });
  } catch {
    return iso;
  }
}
